/**
 * @module core/aem
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

// Core Libraries
import { Component, OnInit, OnDestroy, Output, Input, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { AppConfig } from '@root/app.config';

// Interface
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { PLAN_DETAILS_CONSTANTS } from '@experience/shop/plan-details/constants/product-details-constant';
import { map, takeUntil } from 'rxjs/operators';
import { APIError } from '@support/api/common/error.interface';

@Component({
  selector: 'tmo-digital-customer-authentication-modal',
  templateUrl: './digital-customer-authentication-modal.component.html',
  providers: [],
  styleUrls: [
    '../../../../../../style-sheets/components/modal.component.scss',
    '../../../../../../style-sheets/components/digital-customer-authentication-modal.component.scss',
  ],
})
export class DigitalCustomerAuthenticationModalComponent
  extends PDLBaseComponent
  implements OnInit, OnDestroy
{
  /**
   * To create the modal
   */
  public modal: DynamicModal;
  /**
   * ngUnsubscribe subject for unscribe
   */
  private ngUnsubscribe: Subject<void>;
  /**
   *  to show existing customer modal
   */
  public showExistingCustomerModal: boolean;
  /**
   * Stores the content coming from AEM call
   */
  @Input() public aemContent: ConfigData;

  @Input() public productDetail: ProductDetailsDefaultModel;
  /**
   * Stores show/hide boolean value for modal
   */
  @Input() public displayModal: boolean;
  /**
   * Event Emitter to trigger events on closing the modal
   * @type {EventEmitter<boolean>}
   * @memberof CustomerAuthenticationModalComponent
   */
  @Output() public closeModal: EventEmitter<boolean>;
  /**
   * Event Emitter to trigger events on newcustomer button on the modal
   * @type {EventEmitter<boolean>}
   * @memberof CustomerAuthenticationModalComponent
   */
  @Output() public newCustomer: EventEmitter<boolean>;

  @Output() public closeExistingCustModal: EventEmitter<boolean>;

  public pdlData: Object;

  public digitalAuth: string;

  public disableClose: boolean;

  /**
   * Stores aem content for plan details page
   */
  public localAemContent: ConfigData;

  public showSpinner: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private appConfig: AppConfig,
    public routeSnapshot: ActivatedRoute,
    public pdlService: PdlService,
    private stateUtilService: StateStoreUtilsService,
    private pdpDataHandlerService: PdpDataHandlerService,
    private contentService: ContentProviderService
  ) {
    super(pdlService, routeSnapshot);
    this.newCustomer = new EventEmitter<boolean>();
    this.closeModal = new EventEmitter<boolean>();
    this.closeExistingCustModal = new EventEmitter<boolean>();
    this.ngUnsubscribe = new Subject<void>();
    this.digitalAuth = 'digital-auth digital';
    this.disableClose = false;
  }

  /**
   * loads the initial state of product offerings component
   * @returns void
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.pdlData = {
      ctaName: 'New Customer',
      product: this.productDetail,
    };
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
    this.getAemContent();
    this.disableClose = this.stateUtilService.getMultiLineScenario();
    this.modal = {
      disableDismiss: false,
      disableEsc: this.disableClose,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: !this.disableClose,
      modalName: 'Are you an existing customer?',
    };
  }
  public getAemContent(): void {
    this.showSpinner = true;
    this.contentService
      .getContentData(PLAN_DETAILS_CONSTANTS.PLAN_DETAILS, PLAN_DETAILS_CONSTANTS.BILLING_TYPE)
      .pipe(
        takeUntil(this.ngUnsubscribe.asObservable()),
        map((contentData: Object) => {
          return <ConfigData>this.contentService.getConfigurableData(contentData);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (contentData: ConfigData) => {
          this.localAemContent = contentData;
          this.showSpinner = false;
        },
        (error: APIError) => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * Method used to destroy data, subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Function used to close ExistingCustomerModal and redirect to login page
   * @return void
   */
  public closeExistingCustomerModal(): void {
    this.closeExistingCustModal.emit(true);
  }

  /**
   * Method to Close CustomerAuthentication Modal
   * @returns void
   */
  public closeCustomerAuthenticationModal(isCloseModal: boolean): void {
    this.closeModal.emit(isCloseModal);
  }

  /**
   * on click of new customer selection
   * returns void
   */
  public onNewCustomerSelection(isCloseModal: boolean): void {
    this.newCustomer.emit(isCloseModal);
  }
}
